/* eslint-disable @typescript-eslint/no-explicit-any */
import { Model } from '@adobe/aem-spa-page-model-manager';
import { ModelProps } from '@adobe/aem-react-editable-components';
import { KEY_BOARD_EVENTS, TrackingPropsInterface } from './enums/enums';

export const canUseDOM = !!(typeof window !== 'undefined' && window.document);

//Utility for converting nested container json to tree format

export type AEMModelType = Model & ModelProps;

export type StringIndexedObject = {
  [key: string]: any;
};

export interface ScrollSettings {
  isNavComponent?: boolean;
  isStickyOnScrollDown?: boolean;
  isStickyOnScrollUp?: boolean;
}

export interface ScrollStoreModel extends Model {
  componentId?: string;
  scrollSetting?: ScrollSettings;
  ':children'?: {
    [key: string]: ScrollStoreModel;
  };
  ':items'?: {
    [key: string]: ScrollStoreModel;
  };
}

export interface AEMModel extends AEMModelType {
  resourceProperties?: any;
  itemKey?: string;
  backgroundMedia?: any;
  trackingProperties?: TrackingPropsInterface;
  componentId?: string;
  scrollSetting?: ScrollSettings;
  ':children'?: {
    [key: string]: ScrollStoreModel;
  };
  ':items'?: {
    [key: string]: ScrollStoreModel;
  };
  styleclass?: string;
}
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface SortedModelArray extends Array<AEMModel> {}
export interface AEMContainerProps {
  cqPath?: string;
  isAuthorMode?: boolean;
}

export const sortAEMModelItemsByOrder = (data: AEMModel): SortedModelArray => {
  if (!data) return [];
  const itemsOrder = data[':itemsOrder'] || data?.cqItemsOrder || [];
  const items = data[':items'] || data?.cqItems || {};
  return itemsOrder?.length
    ? itemsOrder.map((itemKey: string) => {
        return { ...items[itemKey], itemKey };
      })
    : [];
};

//TODO:Importing from headless utils is giving error.So kept here for now. Need to remove after the issue is fixed.
export function transformToCQ(propKey: string) {
  const tempKey = propKey.substring(1);

  return 'cq' + tempKey.substring(0, 1).toUpperCase() + tempKey.substring(1);
}

/**
 * Normalizes trackingProperties to ensure consistent data types
 * Converts string "true"/"false" to boolean true/false
 */
const normalizeTrackingProperties = (trackingProps: any): any => {
  if (!trackingProps || typeof trackingProps !== 'object') {
    return trackingProps;
  }

  const normalized = { ...trackingProps };

  // Automatically normalize any property with string "true"/"false" to boolean
  Object.keys(normalized).forEach(prop => {
    if (normalized[prop] === 'true') {
      normalized[prop] = true;
    } else if (normalized[prop] === 'false') {
      normalized[prop] = false;
    }
    // Keep other values as-is (undefined, null, numbers, actual booleans, etc.)
  });

  return normalized;
};

/**
 * Recursively normalizes trackingProperties in nested objects
 */
const deepNormalizeTrackingProperties = (obj: any): any => {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(deepNormalizeTrackingProperties);
  }

  const normalized = { ...obj };

  // Normalize trackingProperties at this level
  if (normalized.trackingProperties) {
    normalized.trackingProperties = normalizeTrackingProperties(normalized.trackingProperties);
  }

  // Also handle referTrackingProperties (for RAF components)
  if (normalized.referTrackingProperties) {
    normalized.referTrackingProperties = normalizeTrackingProperties(normalized.referTrackingProperties);
  }

  // Recursively process nested objects
  Object.keys(normalized).forEach(key => {
    if (typeof normalized[key] === 'object' && normalized[key] !== null) {
      normalized[key] = deepNormalizeTrackingProperties(normalized[key]);
    }
  });

  return normalized;
};

//To format mock model in local dev, in other envs this formatting is taken care by OOTB fetchModel utility
export const respGridUtil = (item: any, options: { normalizeTracking?: boolean } = {}) => {
  if (!item || !Object.keys(item).length) {
    return { cqPath: '' };
  }

  const keys = Object.getOwnPropertyNames(item);
  const props: any = {};

  keys.forEach((key: string) => {
    const propKey = key.startsWith(':') ? transformToCQ(key) : key;
    props[propKey] = item[key] || '';
  });

  // Apply tracking normalization if requested
  if (options.normalizeTracking) {
    return deepNormalizeTrackingProperties(props);
  }

  return props;
};

/**
 *
 * @param text
 * Remove the parent tag from RTE text
 * <p>Lorem Ipsum has been the <sub>industry's standard</sub> dummy text ever</p>
 * =>Lorem Ipsum has been the <sub>industry's standard</sub> dummy text ever
 */
export const removeParentTagRTE = (textRTE: string): string => {
  return textRTE.substring(textRTE.indexOf('>') + 1, textRTE.lastIndexOf('<'));
};

export const getEventKeyTypes = (e: React.KeyboardEvent<HTMLElement>) => {
  const { ENTER, TAB, ESCAPE, ARROW_UP, ARROW_DOWN, ARROW_RIGHT, ARROW_LEFT, SPACEBAR } = KEY_BOARD_EVENTS ?? {};
  return {
    isEnterKey: e.key === ENTER?.key || e.keyCode === ENTER?.keyCode,
    isTabKey: e.key === TAB?.key || e.keyCode === TAB?.keyCode,
    isEscapseKey: e.key === ESCAPE?.key || e.keyCode === ESCAPE?.keyCode,
    isUpArrowKey: e.key === ARROW_UP?.key || e.keyCode === ARROW_UP.keyCode,
    isDownArrowKey: e.key === ARROW_DOWN?.key || e.keyCode === ARROW_DOWN?.keyCode,
    isRightArrowKey: e.key === ARROW_RIGHT?.key || e.keyCode === ARROW_RIGHT?.keyCode,
    isLeftArrowKey: e.key === ARROW_LEFT?.key || e.keyCode === ARROW_LEFT?.keyCode,
    isSpaceBarKey: e.key === SPACEBAR?.key || e.keyCode === SPACEBAR?.keyCode,
  };
};

/**
 *
 * Throttling any event handler to avoid excessive function calls
 * @param func @param delay
 * @returns the handler function "func" which executes after every 500 ms
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const throttle = (func: any, delay: number) => {
  let timeoutId: NodeJS.Timeout;
  let lastExecTime = 0;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (...args: any[]) => {
    const currentTime = new Date().getTime();
    const elapsedTime = currentTime - lastExecTime;

    if (!timeoutId || elapsedTime >= delay) {
      func.apply(this, args);
      lastExecTime = currentTime;
    }

    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      lastExecTime = new Date().getTime();
    }, delay);
  };
};
export const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * This function updates the keys of an object based on a provided key mapping.
 *
 * @param {StringIndexedObject} aemModelObj - The original object whose keys are to be updated.
 *                                    It must be a non-null object.
 *
 * @throws {Error} Will throw an error if the provided obj or keyMap is not an object or is null.
 *
 * @returns {StringIndexedObject} A new object with updated keys.
 *                                If a key in the original object is found in the keyMap,
 *                                it will be replaced with the corresponding value from the keyMap.
 *                                If a key in the original object is not found in the keyMap,
 *                                it will remain the same.
 *                                Note: The function does not mutate the original object.
 */
export const updateAEMCQKeys = (aemModelObj: StringIndexedObject): StringIndexedObject => {
  const cqKeyMap: { [key: string]: string } = {
    ':items': 'cqItems',
    ':itemsOrder': 'cqItemsOrder',
    ':type': 'cqType',
  };

  if (typeof aemModelObj !== 'object' || aemModelObj === null) {
    throw new Error('Invalid object provided');
  }

  const updatedObject: StringIndexedObject = { ...aemModelObj };

  for (const key in cqKeyMap) {
    if (Object.prototype.hasOwnProperty.call(aemModelObj, key)) {
      const updatedKey = cqKeyMap[key];
      // Only update if updatedKey is defined and does not exist in the original object
      if (updatedKey && !Object.prototype.hasOwnProperty.call(aemModelObj, updatedKey)) {
        updatedObject[updatedKey] = aemModelObj[key];
        delete updatedObject[key];
      }
    }
  }

  return updatedObject;
};

export const getLoyaltyTheme = (inputString: string | undefined) => {
  const regex = /\b(memberSolid|memberSolidAlt|silverSolid|goldSolid|platinumSolid|titaniumSolid|ambassadorSolid)\b/g;
  const matches = inputString?.match(regex);

  if (matches) {
    const key = matches[0];
    return key;
  } else {
    return;
  }
};

// This helper maps the current portfolio brand logo tag values (CSS class names) to the future SVG-based logo implementation, where CSS classes will no longer be used. This mapping will be removed once the tag values are updated to SVG file names. This will enable the use of the new logo container without having to re-author the components that are using logo tags
export const getLogoName = (brandTag: string) => {
  const brandTagToLogoNameMap: Record<string, string> = {
    'portfolio-icon-ritz-carlton': 'rz',
    'portfolio-icon-edition': 'eb',
    'portfolio-icon-jw': 'jw',
    'portfolio-icon-autograph-collection': 'ak',
    'portfolio-icon-renaissance': 'br',
    'portfolio-icon-delta-hotels': 'de-portfolio',
    'portfolio-icon-marriott': 'mc',
    'portfolio-icon-marriott-executive-apartments': 'er',
    'portfolio-icon-vacation-club': 'mv',
    'portfolio-icon-gaylord': 'ge',
    'portfolio-icon-ac-hotels': 'ar',
    'portfolio-icon-courtyard': 'cy-portfolio',
    'portfolio-icon-residence-inn': 'ri-portfolio',
    'portfolio-icon-springhill-suites': 'sh',
    'portfolio-icon-fairfield-inn': 'fi-portfolio',
    'portfolio-icon-towneplace-suites': 'ts-portfolio',
    'portfolio-icon-protea-hotels': 'pr-portfolio',
    'portfolio-icon-moxy': 'ox',
    'portfolio-icon-st-regis': 'xr',
    'portfolio-icon-luxury-collection': 'lc',
    'portfolio-icon-w-hotels': 'wh-secondary',
    'portfolio-icon-westin': 'wi',
    'portfolio-icon-sheraton': 'si',
    'portfolio-icon-le-meridien': 'md',
    'portfolio-icon-tribute-portfolio': 'tx',
    'portfolio-icon-design-hotels': 'ds',
    'portfolio-icon-aloft': 'al',
    'portfolio-icon-four-points': 'fp-portfolio',
    'portfolio-icon-element': 'el',
    'portfolio-icon-rcr': 'rzr',
    'portfolio-icon-hotels-and-villas': 'NA',
    'portfolio-icon-hvmi': 'hv',
    'portfolio-icon-mvc': 'mvc',
    'portfolio-icon-xe': 'xe',
    'portfolio-icon-ba': 'ba',
    'portfolio-icon-mg': 'mg',
    'portfolio-icon-oc': 'oc-portfolio',
    'portfolio-icon-cm': 'cm',
    'portfolio-icon-tours-activities': 'NA',
    'portfolio-icon-events': 'NA',
    'portfolio-icon-boutiques': 'NA',
    'portfolio-icon-moments': 'NA',
    'portfolio-icon-sheraton-vacation-club': 'sv',
    'portfolio-icon-westin-vacation-club': 'wv',
    'portfolio-icon-xf': 'xf-portfolio',
    'portfolio-icon-se': 'se',
    'portfolio-icon-rs': 'rs',
    'portfolio-icon-bg': 'bg',
    'portfolio-icon-sn': 'sn',
    'brand-logo-MR': 'mr',
    'brand-logo-RZ': 'rz',
    'brand-logo-EB': 'eb',
    'brand-logo-JW': 'jw',
    'brand-logo-AK': 'ak',
    'brand-logo-BR': 'br',
    'brand-logo-DE': 'de',
    'brand-logo-MC': 'mc',
    'brand-logo-ER': 'er',
    'brand-logo-MV': 'mv',
    'brand-logo-GE': 'ge',
    'brand-logo-AR': 'ar',
    'brand-logo-CY': 'cy',
    'brand-logo-RI': 'ri',
    'brand-logo-SH': 'sh',
    'brand-logo-FI': 'fi',
    'brand-logo-TS': 'ts',
    'brand-logo-PR': 'pr',
    'brand-logo-OX': 'ox',
    'brand-logo-XR': 'xr',
    'brand-logo-LC': 'lc',
    'brand-logo-WH': 'wh',
    'brand-logo-WI': 'wi',
    'brand-logo-SI': 'si',
    'brand-logo-MD': 'md',
    'brand-logo-TX': 'tx',
    'brand-logo-DS': 'ds',
    'brand-logo-AL': 'al',
    'brand-logo-FP': 'fp',
    'brand-logo-EL': 'el',
    'brand-logo-BG': 'bg',
    'brand-logo-HV': 'hv',
    'brand-logo-MI': 'mi',
    'brand-logo-XE': 'xe',
    'brand-logo-BA': 'ba',
    'brand-logo-XF': 'xf',
    'brand-logo-SV': 'sv',
    'brand-logo-WV': 'wv',
    'brand-logo-MG': 'mg',
    'brand-logo-RS': 'rs',
    'brand-logo-SN': 'sn',
    'brand-logo-OC': 'oc',
    'brand-logo-SE': 'se',
    'brand-logo-CM': 'cm',
    't-program-logo-m': 'mc',
    't-program-logo-color-s': 'mbs',
    't-program-logo-cn-s': 'mbs-cn',
    'brand-logo-luxury-Mark': 'lygp-secondary',
    'brand-logo-luxury-group': 'lygp',
  };

  return brandTagToLogoNameMap[brandTag] ?? brandTag?.toLowerCase();
};

/**
 * @returns | if the dir is rtl then returns true else false.
 */
export const getDirection = (() => {
  let cached: boolean | null = null;
  return () => {
    if (cached !== null) return cached;
    if (typeof document !== 'undefined') {
      const directionValue = document?.documentElement?.getAttribute('dir');
      cached = directionValue === 'rtl';
      return cached;
    }
    cached = false;
    return cached;
  };
})();

// Function which returns true if the device is iOS
export const isIOSDevice = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const maxTouchPoints =
    (navigator as unknown as { maxTouchPoints?: number; msMaxTouchPoints?: number })?.maxTouchPoints ??
    (navigator as unknown as { maxTouchPoints?: number; msMaxTouchPoints?: number })?.msMaxTouchPoints ??
    0;
  const iOSByUA = /\b(iPad|iPhone|iPod)\b/i.test(ua);
  const iPadOS13Plus = /Macintosh/i.test(ua) && maxTouchPoints > 1;
  return iOSByUA || iPadOS13Plus;
};
